import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import type { I18n } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import {
  IconCheckOutline18,
  IconCircleCheckOutline18,
  IconOpenInNewWindowOutline18,
  IconSplitOutline18,
  IconTrashOutline18,
} from 'nucleo-ui-outline-18'
import { toast } from 'sonner'
import { CatalogStatusPill, CatalogThumb } from '~/components/admin/catalog-bits'
import { DetailBadge } from '~/components/admin/detail-bits'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import { Spinner } from '~/components/ui/spinner'
import { useGrowingPage } from '~/hooks/use-growing-page'
import {
  catalogCategoryLabel,
  catalogDimensionsText,
  catalogMarketTag,
  catalogRoleLabel,
} from '~/lib/catalog-labels'
import { confirm } from '~/lib/confirm'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import type { CatalogDimensionsCm } from '~convex/lib/catalogEnums'

export const Route = createFileRoute('/admin/catalog/review')({
  component: AdminCatalogReview,
  head: () => ({ meta: [{ title: titleFor(msg`Match review`) }] }),
})

const PAGE_SIZE = 20

type QueueRow = FunctionReturnType<
  typeof api.catalogAdminQueries.listPendingMatches
>['page'][number]
type Pending = QueueRow['pending'][number]

const dimensionsText = (i18n: I18n, dimensions: CatalogDimensionsCm | null): string => {
  if (dimensions === null) {
    return '—'
  }
  const text = catalogDimensionsText(i18n, dimensions)
  return text === '' ? '—' : `${text} cm`
}

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/** One side of the comparison: a thumbnail and the fields that tell two products apart. */
const SideFacts = ({
  code,
  dimensionsCm,
  eyebrow,
  imageCount,
  name,
  sourceUrl,
  thumbUrl,
}: {
  code: string | null
  dimensionsCm: CatalogDimensionsCm | null
  eyebrow: string
  imageCount: number
  name: string
  sourceUrl: string
  thumbUrl: string | null
}) => {
  const { i18n } = useLingui()
  return (
    <div className="flex min-w-0 gap-3">
      <CatalogThumb alt="" className="size-16 shrink-0" url={thumbUrl} />
      <div className="flex min-w-0 flex-col gap-0.5 text-sm">
        <span className="text-muted-foreground text-xs uppercase tracking-wide">{eyebrow}</span>
        <span className="truncate font-medium">{name}</span>
        <span className="truncate text-muted-foreground text-xs">
          {code ?? '—'} · {dimensionsText(i18n, dimensionsCm)} · {i18n.number(imageCount)} img
        </span>
        <a
          className="inline-flex max-w-full items-center gap-1 text-primary text-xs hover:underline"
          href={sourceUrl}
          rel="noreferrer"
          target="_blank"
        >
          <span className="truncate">{hostOf(sourceUrl)}</span>
          <IconOpenInNewWindowOutline18 className="size-3 shrink-0" />
        </a>
      </div>
    </div>
  )
}

/** One pending observation with its Split / Discard actions. */
const PendingRow = ({
  canSplit,
  pending,
  productId,
}: {
  canSplit: boolean
  pending: Pending
  productId: Id<'catalogProducts'>
}) => {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { mutateAsync: split, isPending: isSplitting } = useMutation({
    mutationFn: useConvexMutation(api.catalogAdmin.splitObservation),
  })
  const { mutateAsync: discard, isPending: isDiscarding } = useMutation({
    mutationFn: useConvexMutation(api.catalogAdmin.discardObservation),
  })
  const isBusy = isSplitting || isDiscarding

  const onSplit = async () => {
    const confirmed = await confirm({
      confirmLabel: t`Split`,
      description: t`“${pending.name}” becomes its own product with this page, its history and the images only it carried.`,
      icon: <IconSplitOutline18 />,
      title: t`Split this page into a new product?`,
    })
    if (!confirmed) {
      return
    }
    try {
      const { newProductId } = await split({ observationId: pending._id, productId })
      toast.success(t`Split into a new product.`)
      void navigate({ params: { productId: newProductId }, to: '/admin/catalog/$productId' })
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t`Couldn't split the page.`)
    }
  }
  const onDiscard = async () => {
    const confirmed = await confirm({
      confirmLabel: t`Discard`,
      description: t`The page ${hostOf(pending.sourceUrl)}, its observation history and the images only it carried are deleted. The mirrored files stay in storage.`,
      destructive: true,
      icon: <IconTrashOutline18 />,
      title: t`Discard this page?`,
    })
    if (!confirmed) {
      return
    }
    try {
      await discard({ observationId: pending._id, productId })
      toast.success(t`Page discarded.`)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t`Couldn't discard the page.`)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2">
      <div className="min-w-0 flex-1">
        <SideFacts
          code={pending.code}
          dimensionsCm={pending.dimensionsCm}
          eyebrow={`${catalogRoleLabel(i18n, pending.role)} · ${catalogMarketTag(pending.market)} · ${formatRelativeTime(pending.fetchedAt, i18n)}`}
          imageCount={pending.imageCount}
          name={pending.name}
          sourceUrl={pending.sourceUrl}
          thumbUrl={pending.thumbUrl}
        />
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          disabled={isBusy || !canSplit}
          onClick={onSplit}
          size="sm"
          title={canSplit ? undefined : t`The product’s only page cannot be split off`}
          type="button"
          variant="outline"
        >
          <IconSplitOutline18 />
          <Trans>Split</Trans>
        </Button>
        <Button
          disabled={isBusy || !canSplit}
          onClick={onDiscard}
          size="sm"
          title={
            canSplit
              ? undefined
              : t`The product’s only page cannot be discarded; hide the product instead`
          }
          type="button"
          variant="destructive"
        >
          <IconTrashOutline18 />
          <Trans>Discard</Trans>
        </Button>
      </div>
    </div>
  )
}

/** One product in the queue: the projected row on top, its pending pages beneath, Confirm on the right. */
const QueueItem = ({ row }: { row: QueueRow }) => {
  const { t, i18n } = useLingui()
  const { mutateAsync: confirmMatch, isPending } = useMutation({
    mutationFn: useConvexMutation(api.catalogAdmin.confirmMatch),
  })
  const onConfirm = async () => {
    try {
      await confirmMatch({ productId: row.product._id })
      toast.success(t`Confirmed as one product.`)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t`Couldn't confirm the match.`)
    }
  }
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <SideFacts
            code={row.product.code}
            dimensionsCm={row.product.dimensionsCm}
            eyebrow={`${row.product.brandName} · ${catalogCategoryLabel(i18n, row.product.category)}`}
            imageCount={row.product.imageCount}
            name={row.product.name}
            sourceUrl={row.product.sourceUrl}
            thumbUrl={row.product.thumbUrl}
          />
          <div className="flex flex-wrap items-center gap-2 pl-19">
            <CatalogStatusPill status={row.product.status} />
            <DetailBadge>{t`${i18n.number(row.urlCount)} pages`}</DetailBadge>
            <Link
              className="text-primary text-xs hover:underline"
              params={{ productId: row.product._id }}
              to="/admin/catalog/$productId"
            >
              <Trans>Open product</Trans>
            </Link>
          </div>
        </div>
        <Button disabled={isPending} onClick={onConfirm} size="sm" type="button">
          <IconCheckOutline18 />
          <Trans>Confirm one product</Trans>
        </Button>
      </div>
      {row.pending.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          <Trans>No pending page remains on this row. Confirming clears it from the queue.</Trans>
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {row.pending.map((pending) => (
            <PendingRow
              canSplit={row.urlCount > 1}
              key={pending._id}
              pending={pending}
              productId={row.product._id}
            />
          ))}
        </div>
      )}
    </li>
  )
}

/**
 * `/admin/catalog/review` — products whose identity rests on a fuzzy link,
 * oldest first. Each shows the row as projected and, beneath, the pages the
 * tools were unsure about; a human confirms the whole row, or splits or
 * discards a page.
 */
function AdminCatalogReview() {
  const { t, i18n } = useLingui()
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data } = useQuery(
    convexQuery(api.catalogAdminQueries.listPendingMatches, { paginationOpts })
  )
  const { data: count } = useQuery(convexQuery(api.catalogAdminQueries.reviewCount, {}))

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/catalog">{t`Product catalog`}</BackLink>}
        meta={
          count === undefined
            ? undefined
            : t`${count.truncated ? `${i18n.number(count.count)}+` : i18n.number(count.count)} to review`
        }
        title={t`Match review`}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
        {data === undefined ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : data.page.length === 0 ? (
          <EmptyState
            description={t`Every fuzzy link the tools flagged has been reviewed.`}
            icon={IconCircleCheckOutline18}
            title={t`Nothing to review`}
          />
        ) : (
          <>
            <ol className="flex flex-col gap-3 py-4">
              {data.page.map((row) => (
                <QueueItem key={row.product._id} row={row} />
              ))}
            </ol>
            {data.isDone ? null : (
              <div className="flex justify-center py-3">
                <Button onClick={loadMore} size="sm" type="button" variant="outline">
                  <Trans>Load more</Trans>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
